import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import SidebarLayout from "./SidebarLayout";
import "./shopStyles.css";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://api.ogestyle.com";

const Managers = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [locations, setLocations] = useState([]);
  const [selectedLocationId, setSelectedLocationId] = useState("");
  const [staff, setStaff] = useState([]);
  const [pendingInvitations, setPendingInvitations] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingManager, setEditingManager] = useState(null);
  const [actionLoadingId, setActionLoadingId] = useState("");
  const [inviteActionLoadingId, setInviteActionLoadingId] = useState("");
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    locationId: "",
  });
  const [editForm, setEditForm] = useState({
    firstName: "",
    lastName: "",
    phone: "",
  });

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("ogestyle_token");
    if (!token) {
      navigate("/login");
      return null;
    }
    return { Authorization: `Bearer ${token}` };
  };

  const fetchData = async (locationIdOverride = "") => {
    const headers = getAuthHeaders();
    if (!headers) return;

    try {
      setLoading(true);
      const shopsResponse = await axios.get(`${API_BASE_URL}/api/shop/my-shops`, {
        headers,
      });

      const userShops = Array.isArray(shopsResponse.data)
        ? shopsResponse.data
        : shopsResponse.data.shops || [];
      setLocations(userShops);

      if (userShops.length === 0) {
        setStaff([]);
        setPendingInvitations([]);
        setSelectedLocationId("");
        return;
      }

      const activeLocationId =
        locationIdOverride || selectedLocationId || userShops[0]._id;
      setSelectedLocationId(activeLocationId);

      const [staffResponse, invitesResponse] = await Promise.all([
        axios.get(
          `${API_BASE_URL}/api/shops/${activeLocationId}/staff?includeManagers=true`,
          {
          headers,
          },
        ),
        axios.get(
          `${API_BASE_URL}/api/shops/${activeLocationId}/staff/invitations/pending`,
          { headers },
        ),
      ]);

      setStaff(staffResponse.data.staff || []);
      setPendingInvitations(invitesResponse.data.pendingInvitations || []);
    } catch (error) {
      setStaff([]);
      setPendingInvitations([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const managers = useMemo(() => {
    return staff.filter((member) => member.role === "R2");
  }, [staff]);

  const pendingManagers = useMemo(() => {
    return pendingInvitations.filter(
      (invite) => invite.roleCode === "R2" || invite.role === "R2",
    );
  }, [pendingInvitations]);

  const getManagerLocation = (manager) => {
    const locationId = manager?.activeLocationId || selectedLocationId;
    const location = locations.find((loc) => loc._id === locationId);

    if (!location) {
      return "N/A";
    }

    return `${location.city || ""}${location.city && location.state ? ", " : ""}${location.state || ""}` || "N/A";
  };

  const resetForm = () => {
    setForm({
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      locationId: selectedLocationId || "",
    });
  };

  const openAddModal = () => {
    resetForm();
    setShowAddModal(true);
  };

  const openEditModal = (manager) => {
    setEditingManager(manager);
    setEditForm({
      firstName: manager.userId?.firstName || "",
      lastName: manager.userId?.lastName || "",
      phone: manager.userId?.phone || "",
    });
    setShowEditModal(true);
  };

  const handleUpdateManager = async () => {
    const headers = getAuthHeaders();
    if (!headers || !editingManager?._id) return;

    if (!editForm.firstName || !editForm.lastName) {
      alert("First name and last name are required.");
      return;
    }

    try {
      setSubmitting(true);
      const activeLocationId = editingManager.activeLocationId || selectedLocationId;

      const response = await axios.patch(
        `${API_BASE_URL}/api/shops/${activeLocationId}/staff/${editingManager._id}/profile`,
        {
          firstName: editForm.firstName,
          lastName: editForm.lastName,
          phone: editForm.phone,
        },
        { headers },
      );

      alert(response.data?.message || "Manager updated successfully.");
      setShowEditModal(false);
      setEditingManager(null);
      await fetchData(activeLocationId);
    } catch (error) {
      alert(error.response?.data?.message || "Failed to update manager.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleManagerStatus = async (manager) => {
    const headers = getAuthHeaders();
    if (!headers) return;

    const nextStatus = manager.status === "Active" ? "Inactive" : "Active";
    const actionLabel = nextStatus === "Inactive" ? "deactivate" : "reactivate";

    if (
      !window.confirm(
        `Are you sure you want to ${actionLabel} ${manager.userId?.firstName || "this manager"}?`,
      )
    ) {
      return;
    }

    try {
      setActionLoadingId(manager._id);
      const activeLocationId = manager.activeLocationId || selectedLocationId;
      const response = await axios.patch(
        `${API_BASE_URL}/api/shops/${activeLocationId}/staff/${manager._id}/status`,
        { status: nextStatus },
        { headers },
      );

      alert(response.data?.message || "Manager status updated.");
      await fetchData(activeLocationId);
    } catch (error) {
      alert(error.response?.data?.message || "Failed to update manager status.");
    } finally {
      setActionLoadingId("");
    }
  };

  const handleCancelInvitation = async (invite) => {
    const headers = getAuthHeaders();
    if (!headers) return;

    if (!window.confirm(`Cancel invitation for ${invite.email}?`)) {
      return;
    }

    try {
      setInviteActionLoadingId(invite._id);
      await axios.delete(
        `${API_BASE_URL}/api/shops/${selectedLocationId}/staff/invitations/${invite._id}`,
        { headers },
      );
      alert("Invitation cancelled successfully.");
      await fetchData(selectedLocationId);
    } catch (error) {
      alert(error.response?.data?.message || "Failed to cancel invitation.");
    } finally {
      setInviteActionLoadingId("");
    }
  };

  const handleResendInvitation = async (invite) => {
    const headers = getAuthHeaders();
    if (!headers) return;

    if (!window.confirm(`Resend invitation to ${invite.email}?`)) {
      return;
    }

    try {
      setInviteActionLoadingId(invite._id);

      // Replace old invitation with a fresh one/token.
      await axios.delete(
        `${API_BASE_URL}/api/shops/${selectedLocationId}/staff/invitations/${invite._id}`,
        { headers },
      );

      await axios.post(
        `${API_BASE_URL}/api/staff/invite`,
        {
          firstName: invite.firstName,
          lastName: invite.lastName,
          email: invite.email,
          phone: invite.phone || "",
          roleCode: "R2",
          shopId: selectedLocationId,
        },
        { headers },
      );

      alert("Invitation resent successfully.");
      await fetchData(selectedLocationId);
    } catch (error) {
      alert(error.response?.data?.message || "Failed to resend invitation.");
    } finally {
      setInviteActionLoadingId("");
    }
  };

  const handleAddManager = async () => {
    const headers = getAuthHeaders();
    if (!headers) return;

    if (!form.firstName || !form.lastName || !form.email || !form.locationId) {
      alert("Please fill in all required fields.");
      return;
    }

    try {
      setSubmitting(true);
      const response = await axios.post(
        `${API_BASE_URL}/api/staff/add`,
        {
          firstName: form.firstName,
          lastName: form.lastName,
          email: form.email,
          phone: form.phone,
          roleCode: "R2",
          shopId: form.locationId,
        },
        { headers },
      );

      alert(response.data?.message || "Manager added successfully.");
      setShowAddModal(false);
      resetForm();
      await fetchData(form.locationId);
    } catch (error) {
      alert(error.response?.data?.message || "Failed to add manager.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <SidebarLayout>
        <div className="dashboard-content managers-page">
          <h2>Managers</h2>
          <p>Loading managers...</p>
        </div>
      </SidebarLayout>
    );
  }

  return (
    <SidebarLayout>
      <div
        className="dashboard-content managers-page"
        style={{
          width: "100%",
          maxWidth: "100%",
          boxSizing: "border-box",
          overflowX: "hidden",
        }}
      >
        {locations.length > 1 && (
          <div style={{ marginBottom: "20px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontWeight: "600",
                fontSize: "13px",
                color: "#374151",
              }}
            >
              Select Location
            </label>
            <select
              value={selectedLocationId}
              onChange={(e) => {
                const nextLocationId = e.target.value;
                setSelectedLocationId(nextLocationId);
                fetchData(nextLocationId);
              }}
              style={{
                width: "100%",
                maxWidth: "360px",
                minWidth: 0,
                padding: "10px 14px",
                border: "1px solid #d1d5db",
                borderRadius: "8px",
                fontSize: "14px",
                fontWeight: "500",
                cursor: "pointer",
              }}
            >
              {locations.map((location) => (
                <option key={location._id} value={location._id}>
                  {location.name} - {location.city}, {location.state}
                </option>
              ))}
            </select>
          </div>
        )}

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: isMobile ? "stretch" : "center",
            gap: "12px",
            flexWrap: "wrap",
            marginBottom: "20px",
          }}
        >
          <h2 style={{ margin: 0, fontSize: isMobile ? "20px" : "24px" }}>
            Managers
          </h2>
          <button
            className="btn-primary"
            style={{
              background: "#2563eb",
              padding: "10px 20px",
              fontSize: "14px",
              flex: isMobile ? "1 1 auto" : "none",
            }}
            onClick={openAddModal}
          >
            + Add Manager
          </button>
        </div>

        {managers.length === 0 ? (
          <div className="dashboard-card" style={{ textAlign: "center", padding: "36px" }}>
            <p style={{ margin: 0, color: "#6b7280" }}>
              No managers found for this location.
            </p>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
            {managers.map((manager) => (
              <div
                key={manager._id}
                className="dashboard-card"
                style={{
                  width: "100%",
                  maxWidth: "100%",
                  boxSizing: "border-box",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: isMobile
                      ? "1fr"
                      : "repeat(auto-fit, minmax(180px, 1fr))",
                    gap: "12px",
                    minWidth: 0,
                  }}
                >
                  <div>
                    <p style={{ margin: "0 0 4px 0", fontSize: "11px", color: "#6b7280" }}>
                      Name
                    </p>
                    <p style={{ margin: 0, fontWeight: "600", overflowWrap: "anywhere" }}>
                      {manager.userId?.firstName} {manager.userId?.lastName}
                    </p>
                  </div>
                  <div>
                    <p style={{ margin: "0 0 4px 0", fontSize: "11px", color: "#6b7280" }}>
                      Email
                    </p>
                    <p style={{ margin: 0, overflowWrap: "anywhere" }}>
                      {manager.userId?.email || "N/A"}
                    </p>
                  </div>
                  <div>
                    <p style={{ margin: "0 0 4px 0", fontSize: "11px", color: "#6b7280" }}>
                      Phone
                    </p>
                    <p style={{ margin: 0, overflowWrap: "anywhere" }}>
                      {manager.userId?.phone || "N/A"}
                    </p>
                  </div>
                  <div>
                    <p style={{ margin: "0 0 4px 0", fontSize: "11px", color: "#6b7280" }}>
                      Location
                    </p>
                    <p style={{ margin: 0, overflowWrap: "anywhere" }}>
                      {getManagerLocation(manager)}
                    </p>
                  </div>
                  <div>
                    <p style={{ margin: "0 0 4px 0", fontSize: "11px", color: "#6b7280" }}>
                      Status
                    </p>
                    <p style={{ margin: 0 }}>
                      <span
                        style={{
                          padding: "4px 10px",
                          borderRadius: "999px",
                          background:
                            manager.status === "Active" ? "#ecfdf5" : "#fef3c7",
                          color:
                            manager.status === "Active" ? "#059669" : "#92400e",
                          fontSize: "12px",
                          fontWeight: "600",
                        }}
                      >
                        {manager.status || "Active"}
                      </span>
                    </p>
                  </div>
                  <div>
                    <p style={{ margin: "0 0 4px 0", fontSize: "11px", color: "#6b7280" }}>
                      Actions
                    </p>
                    <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                      <button
                        onClick={() => openEditModal(manager)}
                        style={{
                          padding: "6px 12px",
                          border: "1px solid #d1d5db",
                          borderRadius: "6px",
                          background: "white",
                          color: "#374151",
                          fontSize: "12px",
                          fontWeight: "600",
                          cursor: "pointer",
                        }}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleToggleManagerStatus(manager)}
                        disabled={actionLoadingId === manager._id}
                        style={{
                          padding: "6px 12px",
                          border: "none",
                          borderRadius: "6px",
                          background:
                            manager.status === "Active" ? "#ef4444" : "#10b981",
                          color: "white",
                          fontSize: "12px",
                          fontWeight: "600",
                          cursor:
                            actionLoadingId === manager._id ? "not-allowed" : "pointer",
                          opacity: actionLoadingId === manager._id ? 0.7 : 1,
                        }}
                      >
                        {actionLoadingId === manager._id
                          ? "Working..."
                          : manager.status === "Active"
                            ? "Deactivate"
                            : "Reactivate"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {pendingManagers.length > 0 && (
          <div style={{ marginTop: "28px" }}>
            <h3
              style={{
                margin: "0 0 12px 0",
                fontSize: "18px",
                fontWeight: "600",
                color: "#111827",
              }}
            >
              Pending Manager Invitations ({pendingManagers.length})
            </h3>
            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              {pendingManagers.map((invite) => (
                <div
                  key={invite._id}
                  className="dashboard-card"
                  style={{
                    background: "#fffbeb",
                    borderLeft: "4px solid #f59e0b",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: "10px",
                      flexWrap: "wrap",
                    }}
                  >
                    <div>
                      <p style={{ margin: "0 0 6px 0", fontWeight: "600" }}>
                        {invite.firstName} {invite.lastName}
                      </p>
                      <p style={{ margin: 0, color: "#6b7280", overflowWrap: "anywhere" }}>
                        {invite.email}
                      </p>
                    </div>
                    <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                      <button
                        onClick={() => handleResendInvitation(invite)}
                        disabled={inviteActionLoadingId === invite._id}
                        style={{
                          padding: "6px 12px",
                          border: "none",
                          borderRadius: "6px",
                          background: "#3b82f6",
                          color: "white",
                          fontSize: "12px",
                          fontWeight: "600",
                          cursor:
                            inviteActionLoadingId === invite._id
                              ? "not-allowed"
                              : "pointer",
                          opacity: inviteActionLoadingId === invite._id ? 0.7 : 1,
                        }}
                      >
                        {inviteActionLoadingId === invite._id ? "Working..." : "Resend"}
                      </button>
                      <button
                        onClick={() => handleCancelInvitation(invite)}
                        disabled={inviteActionLoadingId === invite._id}
                        style={{
                          padding: "6px 12px",
                          border: "none",
                          borderRadius: "6px",
                          background: "#ef4444",
                          color: "white",
                          fontSize: "12px",
                          fontWeight: "600",
                          cursor:
                            inviteActionLoadingId === invite._id
                              ? "not-allowed"
                              : "pointer",
                          opacity: inviteActionLoadingId === invite._id ? 0.7 : 1,
                        }}
                      >
                        {inviteActionLoadingId === invite._id ? "Working..." : "Cancel"}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {showEditModal && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0, 0, 0, 0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
            }}
          >
            <div
              style={{
                background: "white",
                borderRadius: "12px",
                padding: "28px",
                width: "92%",
                maxWidth: "500px",
                maxHeight: "90vh",
                overflowY: "auto",
              }}
            >
              <h3 style={{ marginTop: 0, marginBottom: "18px" }}>Edit Manager</h3>

              <div style={{ marginBottom: "14px" }}>
                <label style={{ display: "block", marginBottom: "6px", fontWeight: "600" }}>
                  First Name *
                </label>
                <input
                  type="text"
                  value={editForm.firstName}
                  onChange={(e) =>
                    setEditForm((prev) => ({ ...prev, firstName: e.target.value }))
                  }
                  style={{
                    width: "100%",
                    padding: "10px",
                    border: "1px solid #d1d5db",
                    borderRadius: "6px",
                    fontSize: "14px",
                  }}
                />
              </div>

              <div style={{ marginBottom: "14px" }}>
                <label style={{ display: "block", marginBottom: "6px", fontWeight: "600" }}>
                  Last Name *
                </label>
                <input
                  type="text"
                  value={editForm.lastName}
                  onChange={(e) =>
                    setEditForm((prev) => ({ ...prev, lastName: e.target.value }))
                  }
                  style={{
                    width: "100%",
                    padding: "10px",
                    border: "1px solid #d1d5db",
                    borderRadius: "6px",
                    fontSize: "14px",
                  }}
                />
              </div>

              <div style={{ marginBottom: "22px" }}>
                <label style={{ display: "block", marginBottom: "6px", fontWeight: "600" }}>
                  Phone
                </label>
                <input
                  type="tel"
                  value={editForm.phone}
                  onChange={(e) =>
                    setEditForm((prev) => ({ ...prev, phone: e.target.value }))
                  }
                  style={{
                    width: "100%",
                    padding: "10px",
                    border: "1px solid #d1d5db",
                    borderRadius: "6px",
                    fontSize: "14px",
                  }}
                />
              </div>

              <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingManager(null);
                  }}
                  disabled={submitting}
                  style={{
                    padding: "10px 18px",
                    background: "white",
                    border: "1px solid #d1d5db",
                    borderRadius: "6px",
                    cursor: "pointer",
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateManager}
                  disabled={submitting}
                  style={{
                    padding: "10px 18px",
                    background: submitting ? "#93c5fd" : "#2563eb",
                    color: "white",
                    border: "none",
                    borderRadius: "6px",
                    cursor: submitting ? "not-allowed" : "pointer",
                    fontWeight: "600",
                  }}
                >
                  {submitting ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </div>
          </div>
        )}

        {showAddModal && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0, 0, 0, 0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
            }}
          >
            <div
              style={{
                background: "white",
                borderRadius: "12px",
                padding: "28px",
                width: "92%",
                maxWidth: "500px",
                maxHeight: "90vh",
                overflowY: "auto",
              }}
            >
              <h3 style={{ marginTop: 0, marginBottom: "18px" }}>Add Manager</h3>

              <div style={{ marginBottom: "14px" }}>
                <label style={{ display: "block", marginBottom: "6px", fontWeight: "600" }}>
                  First Name *
                </label>
                <input
                  type="text"
                  value={form.firstName}
                  onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                  style={{
                    width: "100%",
                    padding: "10px",
                    border: "1px solid #d1d5db",
                    borderRadius: "6px",
                    fontSize: "14px",
                  }}
                />
              </div>

              <div style={{ marginBottom: "14px" }}>
                <label style={{ display: "block", marginBottom: "6px", fontWeight: "600" }}>
                  Last Name *
                </label>
                <input
                  type="text"
                  value={form.lastName}
                  onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                  style={{
                    width: "100%",
                    padding: "10px",
                    border: "1px solid #d1d5db",
                    borderRadius: "6px",
                    fontSize: "14px",
                  }}
                />
              </div>

              <div style={{ marginBottom: "14px" }}>
                <label style={{ display: "block", marginBottom: "6px", fontWeight: "600" }}>
                  Email *
                </label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  style={{
                    width: "100%",
                    padding: "10px",
                    border: "1px solid #d1d5db",
                    borderRadius: "6px",
                    fontSize: "14px",
                  }}
                />
              </div>

              <div style={{ marginBottom: "14px" }}>
                <label style={{ display: "block", marginBottom: "6px", fontWeight: "600" }}>
                  Phone (optional)
                </label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  style={{
                    width: "100%",
                    padding: "10px",
                    border: "1px solid #d1d5db",
                    borderRadius: "6px",
                    fontSize: "14px",
                  }}
                />
              </div>

              <div style={{ marginBottom: "22px" }}>
                <label style={{ display: "block", marginBottom: "6px", fontWeight: "600" }}>
                  Location *
                </label>
                <select
                  value={form.locationId}
                  onChange={(e) => setForm({ ...form, locationId: e.target.value })}
                  style={{
                    width: "100%",
                    padding: "10px",
                    border: "1px solid #d1d5db",
                    borderRadius: "6px",
                    fontSize: "14px",
                  }}
                >
                  <option value="">Select location...</option>
                  {locations.map((location) => (
                    <option key={location._id} value={location._id}>
                      {location.name} - {location.city}, {location.state}
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    resetForm();
                  }}
                  disabled={submitting}
                  style={{
                    padding: "10px 18px",
                    background: "white",
                    border: "1px solid #d1d5db",
                    borderRadius: "6px",
                    cursor: "pointer",
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddManager}
                  disabled={submitting}
                  style={{
                    padding: "10px 18px",
                    background: submitting ? "#93c5fd" : "#2563eb",
                    color: "white",
                    border: "none",
                    borderRadius: "6px",
                    cursor: submitting ? "not-allowed" : "pointer",
                    fontWeight: "600",
                  }}
                >
                  {submitting ? "Adding..." : "Add Manager"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </SidebarLayout>
  );
};

export default Managers;
